//@flow
// ----------------------------------------------------------------------
// <copyright file="Izenda.ExportingContainer.js" company="Izenda">
//  Copyright (c) 2015 Izenda, Inc.
//  ALL RIGHTS RESERVED
//
//  The entire contents of this file is protected by U.S. and
//  International Copyright Laws. Unauthorized reproduction,
//  reverse-engineering, and distribution of all or any portion of
//  the code contained in this file is strictly prohibited and may
//  result in severe civil and criminal penalties and will be
//  prosecuted to the maximum extent possible under the law.
//
//  RESTRICTIONS
//
//  THIS SOURCE CODE AND ALL RESULTING INTERMEDIATE FILES
//  ARE CONFIDENTIAL AND PROPRIETARY TRADE
//  SECRETS OF IZENDA INC. THE REGISTERED DEVELOPER IS
//  LICENSED TO DISTRIBUTE THE PRODUCT AND ALL ACCOMPANYING .NET
//  CONTROLS AS PART OF AN EXECUTABLE PROGRAM ONLY.
//
//  THE SOURCE CODE CONTAINED WITHIN THIS FILE AND ALL RELATED
//  FILES OR ANY PORTION OF ITS CONTENTS SHALL AT NO TIME BE
//  COPIED, TRANSFERRED, SOLD, DISTRIBUTED, OR OTHERWISE MADE
//  AVAILABLE TO OTHER INDIVIDUALS WITHOUT EXPRESS WRITTEN CONSENT
//  AND PERMISSION FROM IZENDA INC.
//
//  CONSULT THE END USER LICENSE AGREEMENT(EULA FOR INFORMATION ON
//  ADDITIONAL RESTRICTIONS.
//
// </copyright>
// ----------------------------------------------------------------------

import React, { Component, PropTypes } from 'react';
import { findDOMNode } from 'react-dom';
import PrintingUtils from 'share/utils/Izenda.PrintingUtils';
import PrintConstants from 'logic/data/Izenda.PrintConstants';
import resizeDetector from 'share/utils/Izenda.SizeDetector';
import {
  ORIENTATION_VALUE,
  MARGIN_VALUE,
  COLS_LAYOUT,
  MARGIN_KEY,
  MARGIN_INPUT,
  MAX_HEIGHT
} from 'logic/data/Izenda.ExportConstants';
import GridLayout from 'share/components/controls/Izenda.GridLayout';
import PrintablePage from 'share/components/controls/Izenda.PrintablePage';
import Label from 'share/components/controls/Izenda.Label';
import ExportingConfigAction from './Izenda.ExportingConfigAction';
import connect from 'share/enhancers/Izenda.Connect';
import { observer } from 'mobx-react';
import classnames from 'classnames';
import NumericInput from 'share/components/controls/Izenda.NumericInput';
import Validator from 'share/components/hoc/Izenda.Validator';
import ReportBodyConstants from 'logic/data/Izenda.ReportBodyConstants';

import { getOr, map, sortBy } from 'lodash/fp';

const LEFT = 'left';
const RIGHT = 'right';
const FORMAT = '#,###.00';
const ValidatorNumbericInput = Validator()(NumericInput);

/**
 * Left/top/... margins area component
 */
const MarginInfo = observer((props: any) => {
  const { name, className, position, value: key, model, model: { margins, currentMarginSetting } } = props;
  const onChange = val => {
    props.onChange(val, key);
  };

  const max = model.getMaxByKey(key) || MAX_HEIGHT;

  const marginLabel = <Label>{name}</Label>;
  const marginInput = (
    <ValidatorNumbericInput
      disabled={margins !== MARGIN_VALUE.CUSTOM}
      value={currentMarginSetting[key]}
      onChange={onChange}
      format={FORMAT}
      field={key}
      containerClassName="margin-config"
      max={max}
      min={0}
    />
  );

  return (
    <div className="u_tableRow">
      <div className={classnames(className, 'u_tableCell')}>
        {position === LEFT ? marginLabel : marginInput}
        {position === RIGHT ? marginLabel : marginInput}
      </div>
    </div>
  );
});
MarginInfo.displayName = 'MarginInfo';

@connect(ExportingConfigAction)
export default class ExportingContainer extends Component {
  static PropTypes = {
    model: PropTypes.object.isRequired
  };

  state = {
    printablePageOuterWidth: 'calc(100% - 20px)',
    printablePageOuterHeight: 'calc(100% - 20px)'
  };

  setPrintablePageOuter = (printablePageOuter: any) => {
    this.printablePageOuter = printablePageOuter;
  };

  getRootElement = () => {
    return findDOMNode(this.printablePageOuter) || null;
  };

  /**
   * Calculate print previuew container size.
   * @param {number|null} newWidth New width (null means 100%)
   * @param {number|null} newHeight New height (null means 100%)
   * @returns {{newWidth: number|null, newHeight: number|null}}
   */
  updateOuterState = (newWidth, newHeight) => {
    const { printablePageOuterWidth, printablePageOuterHeight } = this.state;
    const newWidthStr = newWidth ? `${newWidth}px` : 'calc(100% - 20px)';
    const newHeightStr = newHeight ? `${newHeight}px` : 'calc(100% - 20px)';
    if (newWidthStr !== printablePageOuterWidth || newHeightStr !== printablePageOuterHeight) {
      this.setState({
        printablePageOuterWidth: newWidthStr,
        printablePageOuterHeight: newHeightStr
      });
    }
    return {newWidth, newHeight};
  };

  /**
   * Calculate preview container size
   * @returns {null|{newWidth: (number|null), newHeight: (number|null)}}
   */
  calcOuterSize = () => {
    const {
      model: { orientation }
    } = this.props;

    const rootEl = this.getRootElement();
    if (!rootEl)
      return null;
    if (orientation === ORIENTATION_VALUE.PORTRAIT) {
      return this.updateOuterState(
        Math.floor(rootEl.parentElement.getBoundingClientRect().height / Math.sqrt(2)) - 20,
        null
      );
    } else {
      return this.updateOuterState(
        null,
        Math.floor(rootEl.parentElement.getBoundingClientRect().width / Math.sqrt(2)) - 20
      );
    }
  };

  /**
   * Calculate X-scale to fit it into the preview container.
   */
  getScaleX = () => {
    const {
      model,
      model: {
        pageSizeKey,
        orientation,
        currentMarginSetting
      }
    } = this.props;
    const {newWidth, newHeight} = this.calcOuterSize();
    const {BODY_CELL_WIDTH} = ReportBodyConstants;
    const marginPercents = PrintingUtils.getPageMarginsInPercent(pageSizeKey, orientation, currentMarginSetting);
    const widthOriginal = BODY_CELL_WIDTH * 12 + 13;

    // Calculate new scale x
    let newScaleX = 1;
    let scaleWidth = 0;
    let scaleHeight = 0;
    if (PrintConstants.isPortrait(orientation)) {
      scaleWidth = newWidth * (marginPercents.horizontal.center / 100);
      scaleHeight = newWidth * Math.sqrt(2) * (marginPercents.vertical.center / 100);
      newScaleX = scaleWidth / widthOriginal;
    } else {
      // if we want to fit preview into full width - we should replace "newHeight" -> "newHeight*sqrt(2)"
      scaleWidth = newHeight * Math.sqrt(2) * (marginPercents.horizontal.center / 100);
      scaleHeight = newHeight * (marginPercents.vertical.center / 100);
      newScaleX = (scaleWidth / widthOriginal) / Math.sqrt(2);
    }
    if (model.scaleX !== newScaleX) {
      model.scaleX = newScaleX;
      model.scaleWidth = scaleWidth;
      model.scaleHeight = scaleHeight;
    }
  };

  componentDidMount() {
    this.getScaleX();
    const rootEl = this.getRootElement();
    if (!rootEl.length)
      return;
    resizeDetector.listenTo(rootEl, this.getScaleX);

    this.unlisten = () => {
      resizeDetector.removeListener(rootEl, this.getScaleX);
    };
  }

  componentDidUpdate() {
    this.getScaleX();
  }

  componentWillUnmount() {
    this.unlisten && this.unlisten();
    this.unlisten = null;
    this.printablePageOuter = null;
  }

  render() {
    const {
      changeMarginConfig,
      store: { fieldsStore },
      store,
      model,
      model: { orientation, currentMarginSetting, centerOnPage, pageBreakAfterReportPart }
    } = this.props;

    const printablePageOuterStyle = {
      height: this.state.printablePageOuterHeight,
      width: this.state.printablePageOuterWidth
    };

    // If page break: split into several pages:
    let reportParts = getOr([], 'reportBody.reportParts', fieldsStore);
    reportParts = sortBy(['positionY', 'positionX'], reportParts);
    let reportPartsPerPage = pageBreakAfterReportPart
      ? map(part => [part], reportParts)
      : reportParts.length ? [map(part => part, reportParts)] : [];

    return (
      <div className="izenda-Wrapper-content u_paddingLeftRight izenda-Exporting">
        <GridLayout className="u_fullHeight u-posRelative">
          <GridLayout.Row className="u_fullHeight u-posRelative">
            <GridLayout.Col cols={2} className="u_fullHeight u_table u_textRight">
              <MarginInfo
                value={MARGIN_KEY.TOP}
                className="u_alignTop"
                name={MARGIN_INPUT.TOP}
                model={model}
                onChange={changeMarginConfig}
                position={LEFT}
              />
              <MarginInfo
                value={MARGIN_KEY.LEFT}
                className="u_alignMiddle"
                name={MARGIN_INPUT.LEFT}
                model={model}
                onChange={changeMarginConfig}
                position={LEFT}
              />
              <MarginInfo
                value={MARGIN_KEY.BOTTOM}
                className="u_alignBottom"
                name={MARGIN_INPUT.BOTTOM}
                model={model}
                onChange={changeMarginConfig}
                position={LEFT}
              />
            </GridLayout.Col>
            <GridLayout.Col cols={8} className="u_fullHeight u-posRelative u_noPaddingRight u_noPaddingLeft">
              {/*Outer container which used to calculate current preview size*/}
              <div
                ref={this.setPrintablePageOuter}
                className="u_fullHeight u_noPaddingAll u_noMarginAll"
                style={{'overflowY': 'auto'}}>
                {/*Preview Page*/}
                {reportPartsPerPage.map((parts, index) => (
                  <div
                    key={index}
                    className="izenda-Exporting-report"
                    style={printablePageOuterStyle}>
                    <PrintablePage
                      pageIndex={index}
                      top={currentMarginSetting.top}
                      right={currentMarginSetting.right}
                      bottom={currentMarginSetting.bottom}
                      left={currentMarginSetting.left}
                      header={currentMarginSetting.header}
                      footer={currentMarginSetting.footer}
                      horizontally={centerOnPage.horizontally}
                      vertically={centerOnPage.vertically}
                      orientation={orientation}
                      store={fieldsStore}
                      reportDesignerStore={store}
                      exportFormatSetting={model}
                      partsToRender={parts}
                      pageBreakAfterReportPart={pageBreakAfterReportPart}
                      viewMode={false}
                    />
                  </div>
                ))}
              </div>
            </GridLayout.Col>
            <GridLayout.Col cols={2} className="u_fullHeight u_table">
              <MarginInfo
                value={MARGIN_KEY.HEADER}
                className="u_alignTop"
                name={MARGIN_INPUT.HEADER}
                model={model}
                onChange={changeMarginConfig}
                position={RIGHT}
              />
              <MarginInfo
                value={MARGIN_KEY.RIGHT}
                className="u_alignMiddle"
                name={MARGIN_INPUT.RIGHT}
                model={model}
                onChange={changeMarginConfig}
                position={RIGHT}
              />
              <MarginInfo
                value={MARGIN_KEY.FOOTER}
                className="u_alignBottom"
                name={MARGIN_INPUT.FOOTER}
                model={model}
                onChange={changeMarginConfig}
                position={RIGHT}
              />
            </GridLayout.Col>
          </GridLayout.Row>
        </GridLayout>
      </div>
    );
  }
}



// WEBPACK FOOTER //
// ./ui/modules/ReportDesigner/Exporting/Izenda.ExportingContainer.js